import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  Building2,
  Columns3,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Plus as PlusIcon,
  RefreshCw,
  Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PracticeCountPill, PracticeShareRing } from '../components/pf/PfUsageMetrics';
import api from '../services/api';

const labelRighePerPagina = 'Righe per pagina:';

const labelDaA = ({ from, to, count }) => {
  const total = count !== -1 ? count : `più di ${to}`;
  return `${from}-${to} di ${total}`;
};

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('it-IT', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function PfIstitutiEroganti() {
  const definizioneColonne = useMemo(
    () => [
      { key: 'nome', label: 'Nome', sortable: true, align: 'left' },
      { key: 'codice', label: 'Codice', sortable: true, align: 'left' },
      { key: 'tipo', label: 'Tipo', sortable: true, align: 'left' },
      { key: 'referente', label: 'Referente', sortable: true, align: 'left' },
      { key: 'telefono', label: 'Telefono', sortable: true, align: 'left' },
      { key: 'email', label: 'Email', sortable: true, align: 'left' },
      { key: 'attivo', label: 'Attivo', sortable: true, align: 'left' },
      { key: 'data_inserimento', label: 'Inserito il', sortable: true, align: 'left' },
      { key: 'totale_pratiche_collegate', label: 'Totale pratiche', sortable: true, align: 'right' },
      { key: 'percentuale_sul_totale', label: '% sul totale', sortable: true, align: 'right' }
    ],
    []
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('nome');
  const [filterText, setFilterText] = useState('');
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    'nome',
    'tipo',
    'referente',
    'telefono',
    'email',
    'attivo',
    'totale_pratiche_collegate',
    'percentuale_sul_totale'
  ]);

  const [openDialog, setOpenDialog] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    codice: '',
    tipo: 'banca',
    referente: '',
    telefono: '',
    email: '',
    note: '',
    attivo: true
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await api.get('/pf-istituti-eroganti');
      setRows(response.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching pf istituti eroganti:', err);
      setError('Errore nel caricamento degli istituti eroganti');
      toast.error('Errore nel caricamento degli istituti eroganti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const colonneMostrate = useMemo(() => definizioneColonne.filter((c) => visibleColumns.includes(c.key)), [definizioneColonne, visibleColumns]);

  const righeFiltrate = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const haystack = [r.nome, r.codice, r.tipo, r.referente, r.telefono, r.email].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, filterText]);

  const righeOrdinate = useMemo(() => {
    const stabilized = righeFiltrate.map((el, index) => [el, index]);
    const isAsc = order === 'asc';
    stabilized.sort((a, b) => {
      const rowA = a[0];
      const rowB = b[0];
      const valA = rowA?.[orderBy];
      const valB = rowB?.[orderBy];

      if (orderBy === 'attivo' || orderBy === 'totale_pratiche_collegate' || orderBy === 'percentuale_sul_totale') {
        const numA = Number(valA || 0);
        const numB = Number(valB || 0);
        return isAsc ? numA - numB : numB - numA;
      }

      const strA = (valA || '').toString().toLowerCase();
      const strB = (valB || '').toString().toLowerCase();
      if (strA < strB) return isAsc ? -1 : 1;
      if (strA > strB) return isAsc ? 1 : -1;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  }, [righeFiltrate, order, orderBy]);

  const righePaginazione = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return righeOrdinate.slice(start, end);
  }, [page, rowsPerPage, righeOrdinate]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
    setPage(0);
  };

  const handleToggleColumn = (key) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const handleOpenDialog = (row = null) => {
    if (row) {
      setCurrentRow(row);
      setFormData({
        nome: row.nome || '',
        codice: row.codice || '',
        tipo: row.tipo || 'banca',
        referente: row.referente || '',
        telefono: row.telefono || '',
        email: row.email || '',
        note: row.note || '',
        attivo: Boolean(row.attivo)
      });
    } else {
      setCurrentRow(null);
      setFormData({
        nome: '',
        codice: '',
        tipo: 'banca',
        referente: '',
        telefono: '',
        email: '',
        note: '',
        attivo: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRow(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async () => {
    try {
      if (currentRow) {
        await api.put(`/pf-istituti-eroganti/${currentRow.id}`, formData);
        toast.success('Istituto erogante aggiornato con successo');
      } else {
        await api.post('/pf-istituti-eroganti', formData);
        toast.success('Istituto erogante creato con successo');
      }
      handleCloseDialog();
      fetchRows();
    } catch (err) {
      console.error('Error saving pf istituto erogante:', err);
      const message = err?.response?.data?.message || 'Errore nel salvataggio';
      toast.error(message);
    }
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/pf-istituti-eroganti/${deleteTarget.id}`);
      toast.success('Istituto erogante eliminato con successo');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchRows();
    } catch (err) {
      console.error('Error deleting pf istituto erogante:', err);
      const message = err?.response?.data?.message || 'Errore durante eliminazione';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <Building2 className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800 dark:text-gray-100">
              Pratiche Finanziarie — Istituti eroganti
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Anagrafica banche e finanziarie
            </Typography>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRows} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => handleOpenDialog(null)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuovo
          </Button>
        </div>
      </div>

      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Card className="shadow-sm border-none overflow-hidden rounded-xl">
        <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              value={filterText}
              onChange={handleFilterChange}
              placeholder="Cerca (nome, codice, referente, email, telefono...)"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                )
              }}
              sx={{ width: 360 }}
            />
          </Box>

          <Tooltip title="Colonne">
            <IconButton onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}>
              <Columns3 size={18} />
            </IconButton>
          </Tooltip>
        </Box>

        <Menu anchorEl={columnsMenuAnchor} open={Boolean(columnsMenuAnchor)} onClose={() => setColumnsMenuAnchor(null)}>
          {definizioneColonne.map((col) => {
            const checked = visibleColumns.includes(col.key);
            const disableHide = checked && visibleColumns.length === 1;
            return (
              <MenuItem key={col.key} dense onClick={() => handleToggleColumn(col.key)} disabled={disableHide}>
                <Checkbox size="small" checked={checked} />
                <ListItemText primary={col.label} />
              </MenuItem>
            );
          })}
        </Menu>

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                {colonneMostrate.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    sx={{
                      whiteSpace: 'nowrap',
                      ...(col.key === 'totale_pratiche_collegate' ? { minWidth: 104 } : {}),
                      ...(col.key === 'percentuale_sul_totale' ? { minWidth: 88 } : {})
                    }}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? order : 'asc'}
                        onClick={() => handleRequestSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={colonneMostrate.length + 1} align="center" className="py-8">
                    <RefreshCw className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                  </TableCell>
                </TableRow>
              ) : righePaginazione.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colonneMostrate.length + 1} align="center" className="py-8 text-gray-500">
                    Nessun istituto trovato
                  </TableCell>
                </TableRow>
              ) : (
                righePaginazione.map((row) => (
                  <TableRow key={row.id} hover>
                    {colonneMostrate.map((col) => (
                      <TableCell
                        key={`${row.id}-${col.key}`}
                        align={col.align}
                        sx={{
                          ...(col.key === 'totale_pratiche_collegate' ? { minWidth: 104 } : {}),
                          ...(col.key === 'percentuale_sul_totale' ? { minWidth: 88 } : {})
                        }}
                      >
                        {col.key === 'attivo' ? (
                          <Checkbox checked={Boolean(row.attivo)} disabled />
                        ) : col.key === 'data_inserimento' ? (
                          formatDateTime(row.data_inserimento)
                        ) : col.key === 'totale_pratiche_collegate' ? (
                          <PracticeCountPill value={row.totale_pratiche_collegate} />
                        ) : col.key === 'percentuale_sul_totale' ? (
                          <PracticeShareRing value={row.percentuale_sul_totale} />
                        ) : (
                          row[col.key] ?? '-'
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Modifica">
                          <IconButton size="small" onClick={() => handleOpenDialog(row)}>
                            <EditIcon size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Elimina">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                            <DeleteIcon size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={righeFiltrate.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={labelRighePerPagina}
          labelDisplayedRows={labelDaA}
        />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{currentRow ? 'Modifica istituto erogante' : 'Nuovo istituto erogante'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField name="nome" label="Nome" value={formData.nome} onChange={handleChange} fullWidth required />
            <TextField name="codice" label="Codice" value={formData.codice} onChange={handleChange} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select name="tipo" label="Tipo" value={formData.tipo} onChange={handleChange}>
                <MenuItem value="banca">Banca</MenuItem>
                <MenuItem value="finanziaria">Finanziaria</MenuItem>
                <MenuItem value="altro">Altro</MenuItem>
              </Select>
            </FormControl>
            <TextField name="referente" label="Referente" value={formData.referente} onChange={handleChange} fullWidth />
            <TextField name="telefono" label="Telefono" value={formData.telefono} onChange={handleChange} fullWidth />
            <TextField name="email" label="Email" value={formData.email} onChange={handleChange} fullWidth />
            <TextField name="note" label="Note" value={formData.note} onChange={handleChange} fullWidth multiline minRows={3} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox name="attivo" checked={Boolean(formData.attivo)} onChange={handleCheckboxChange} />
              <Typography variant="body2">Attivo</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={handleCloseDialog}>
            Annulla
          </Button>
          <Button onClick={handleSubmit}>Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Vuoi eliminare l’istituto erogante <strong>{deleteTarget?.nome || ''}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
